/**
 * Leaderboard service — talks to Back4App (a hosted Parse server) via the
 * Parse REST API. We use the "JavaScript Key" since calls originate from
 * the browser; that key is intended for client exposure (it identifies the
 * app to Parse but doesn't grant write access — class-level permissions
 * + the Parse ACL system handle authorization on the server side).
 *
 * Class schema (created automatically on first POST):
 *   GauntletScore
 *     name        String   required, displayed on the leaderboard
 *     email       String   optional, only stored if optInPrize=true
 *     score       Number   total run score
 *     timeMs      Number   total gauntlet duration in milliseconds
 *     optInPrize  Boolean  did the player tick the prize-draw checkbox?
 */

const BASE_URL = 'https://parseapi.back4app.com/classes/GauntletScore';
const APP_ID = (import.meta.env.VITE_BACK4APP_APP_ID as string | undefined) ?? '';
const JS_KEY = (import.meta.env.VITE_BACK4APP_JS_KEY as string | undefined) ?? '';

/** True when both keys are wired up. Lets the UI hide leaderboard features
 *  if the app is running without env vars (e.g., a stripped fork). */
export const isLeaderboardConfigured = (): boolean => Boolean(APP_ID && JS_KEY);

const headers = (): HeadersInit => ({
  'X-Parse-Application-Id': APP_ID,
  'X-Parse-JavaScript-Key': JS_KEY,
  'Content-Type': 'application/json',
});

export interface ScoreSubmission {
  name: string;
  /** Empty string is fine — we only store / use it if optInPrize is true. */
  email: string;
  score: number;
  timeMs: number;
  optInPrize: boolean;
}

export interface LeaderboardEntry {
  objectId: string;
  name: string;
  score: number;
  timeMs: number;
  /** ISO string from Parse's createdAt. */
  createdAt: string;
}

/**
 * POST a new score. Resolves with the new objectId on success. Rejects
 * with a string error message that's safe to show in the UI. Does NOT
 * throw on a 401/403 — instead returns a friendly message; submission
 * is fire-and-forget from the player's perspective so we should never
 * block their celebration on a network hiccup.
 *
 * After a successful Back4App save we ALSO fire a Teams notification via
 * `/api/notify-score` (Vercel serverless function). That call is purely
 * fire-and-forget — failures are logged but never bubble up to the UI,
 * since notification delivery is operationally separate from "did the
 * score actually post to the leaderboard".
 */
export async function submitScore(s: ScoreSubmission): Promise<string> {
  if (!isLeaderboardConfigured()) {
    throw new Error('Leaderboard is not configured (missing API keys).');
  }
  const body = {
    name: s.name.slice(0, 24), // hard cap so a 5000-char name can't break the table
    email: s.optInPrize ? s.email.slice(0, 200) : '',
    score: s.score,
    timeMs: s.timeMs,
    optInPrize: s.optInPrize,
  };
  const res = await fetch(BASE_URL, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`Submit failed (${res.status})`);
  }
  const data = (await res.json()) as { objectId: string };

  // Fire-and-forget Teams notification. Compute rank once so the card
  // can show "1 / 47" alongside the player's score. Wrapped in try/catch
  // so any failure (network, missing function, env var unset locally)
  // doesn't disturb the player's flow.
  void notifyTeams(s).catch((e) => {
    console.warn('Teams notification failed (non-fatal):', e);
  });

  return data.objectId;
}

/** Fire-and-forget POST to /api/notify-score with full payload + rank. */
async function notifyTeams(s: ScoreSubmission): Promise<void> {
  let rank: number | undefined;
  let total: number | undefined;
  try {
    const r = await fetchRankFor(s.score, s.timeMs);
    rank = r.rank;
    total = r.total;
  } catch {
    // Rank fetch is best-effort — Teams card just omits the rank field.
  }
  await fetch('/api/notify-score', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: s.name,
      email: s.email,
      optInPrize: s.optInPrize,
      score: s.score,
      timeMs: s.timeMs,
      rank,
      total,
    }),
  });
}

/**
 * Fetch the top N scores. Sort order:
 *   1. Higher score wins  (-score, descending)
 *   2. On a score tie, faster time wins  (timeMs, ascending)
 *   3. On both tied, whoever got there FIRST wins  (createdAt, ascending)
 *
 * The createdAt tiebreaker matters for arcade-fairness: if two players
 * truly tie on both score and time, the original holder of the slot
 * shouldn't be displaceable by a later identical run.
 *
 * Default 10 entries.
 */
export async function fetchTopScores(limit = 10): Promise<LeaderboardEntry[]> {
  if (!isLeaderboardConfigured()) {
    throw new Error('Leaderboard is not configured (missing API keys).');
  }
  // Parse REST query params. We deliberately do NOT request `email` so it
  // can't be scraped from clients. createdAt is a built-in field on every
  // Parse object so we can sort by it without listing it in `keys`.
  const params = new URLSearchParams({
    order: '-score,timeMs,createdAt',
    limit: String(limit),
    keys: 'name,score,timeMs',
  });
  const res = await fetch(`${BASE_URL}?${params.toString()}`, {
    method: 'GET',
    headers: headers(),
  });
  if (!res.ok) {
    throw new Error(`Leaderboard fetch failed (${res.status})`);
  }
  const data = (await res.json()) as { results: LeaderboardEntry[] };
  return data.results;
}

/** Format a duration in milliseconds as M:SS for leaderboard rendering. */
export function formatTime(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Returns the player's leaderboard rank for a given (score, timeMs):
 * 1-based, where rank N means N-1 entries strictly beat them under the
 * SAME tiebreaker rule the top-10 uses (higher score wins; on a tie,
 * faster time wins). Also returns total entry count for "Rank 28 / 142".
 */
export async function fetchRankFor(
  score: number,
  timeMs: number,
): Promise<{ rank: number; total: number }> {
  if (!isLeaderboardConfigured()) {
    throw new Error('Leaderboard is not configured (missing API keys).');
  }
  // "Beats us" = strictly higher score, OR same score with strictly faster
  // time. Matches the order=-score,timeMs query the top-10 uses, so the
  // rank shown in the footer aligns with the position in the table.
  const beatsUs = {
    $or: [
      { score: { $gt: score } },
      { score: score, timeMs: { $lt: timeMs } },
    ],
  };
  const aboveParams = new URLSearchParams({
    where: JSON.stringify(beatsUs),
    count: '1',
    limit: '0',
  });
  const totalParams = new URLSearchParams({ count: '1', limit: '0' });
  const [aboveRes, totalRes] = await Promise.all([
    fetch(`${BASE_URL}?${aboveParams.toString()}`, { headers: headers() }),
    fetch(`${BASE_URL}?${totalParams.toString()}`, { headers: headers() }),
  ]);
  if (!aboveRes.ok || !totalRes.ok) {
    throw new Error('Rank fetch failed');
  }
  const aboveData = (await aboveRes.json()) as { count: number };
  const totalData = (await totalRes.json()) as { count: number };
  return { rank: aboveData.count + 1, total: totalData.count };
}
